import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { getSiteConfig } from "../services/api";
import { LogoMark, LogoWordmark } from "../components/Logo";
import PlatypusLogo from "../components/PlatypusLogo";
import { OPERATOR } from "../components/LegalPage";

const PILLARS = [
  {
    icon: "bi-search",
    title: "Find",
    text: "Search open roles by title, location, work type and level, with visa sponsorship history for each company.",
  },
  {
    icon: "bi-send",
    title: "Apply",
    text: "Tailor your resume, write a cover letter and fill in the application form in a few clicks. You review and submit.",
  },
  {
    icon: "bi-kanban",
    title: "Track",
    text: "Every job lives on one board, from saved to offer, with reminders for follow-ups and interviews.",
  },
];

const FEATURES = [
  {
    icon: "bi-globe2",
    title: "Job search with sponsorship info",
    text: "Filter by remote, hybrid or on-site, full-time or internship, and see which companies have sponsored H-1B visas before.",
  },
  {
    icon: "bi-file-earmark-text",
    title: "Resumes tailored to each job",
    text: "Plateo rewrites your resume for the job description and keeps your own layout, fonts and formatting.",
  },
  {
    icon: "bi-envelope-paper",
    title: "Cover letters in seconds",
    text: "A cover letter written from your resume and the job posting, ready to edit, download or attach.",
  },
  {
    icon: "bi-puzzle",
    title: "Chrome extension that fills forms",
    text: "Save any job posting with one click and fill in applications on Greenhouse, Ashby, Workday and most other job sites, page by page.",
  },
  {
    platypus: true,
    title: "Platypus, your AI agent",
    text: "Platypus hunts for new jobs that fit you in a loop and prepares each application, every day if you like. Nothing is submitted without you.",
  },
  {
    icon: "bi-check2-circle",
    title: "Marks jobs as applied for you",
    text: "When the extension sees the “thanks for applying” page, the job moves to Applied on your board.",
  },
  {
    icon: "bi-people",
    title: "People to reach out to",
    text: "Find recruiters, hiring managers and senior peers on LinkedIn, add them here, and draft a personal email in your own voice.",
  },
  {
    icon: "bi-calendar-check",
    title: "Know what to do next",
    text: "Your home page lists follow-ups, upcoming interviews and recruiters to email, so nothing slips.",
  },
];

const STEPS = [
  {
    title: "Create your free account",
    text: "Sign up with your email, upload your resume and fill in your profile once.",
  },
  {
    title: "Add the jobs you want",
    text: "Search inside Plateo, paste a job link, or click the extension on any job posting to save it.",
  },
  {
    title: "Prepare your application",
    text: "Tailor your resume and write a cover letter for the job. Edit anything you like.",
  },
  {
    title: "Fill it in and submit",
    text: "Click Open application. Plateo fills in the form, you check it and press submit yourself.",
  },
  {
    title: "Track and follow up",
    text: "Plateo marks the job as applied, reminds you to follow up and helps you email the recruiter.",
  },
];

function Shot({ src, alt, className = "" }) {
  return (
    <div className={`landing-shot ${className}`}>
      <div className="landing-shot-bar">
        <span />
        <span />
        <span />
      </div>
      <img src={src} alt={alt} loading="lazy" />
    </div>
  );
}

function Landing() {
  const { user } = useAuth();
  const [{ extensionUrl, contactEmail }, setConfig] = useState({
    extensionUrl: "",
    contactEmail: "",
  });

  const { hash } = useLocation();

  useEffect(() => {
    getSiteConfig().then(setConfig);
  }, []);

  useEffect(() => {
    const target = hash && document.getElementById(hash.slice(1));
    if (target) target.scrollIntoView();
    else window.scrollTo(0, 0);
  }, [hash]);

  const primaryCta = user ? (
    <Link to="/" className="btn btn-primary btn-lg">
      Open Plateo
    </Link>
  ) : (
    <Link to="/register" className="btn btn-primary btn-lg">
      Create a free account
    </Link>
  );

  return (
    <div className="landing">
      <header className="landing-nav">
        <div className="landing-container landing-nav-inner">
          <Link to={user ? "/welcome" : "/"} className="brand">
            <LogoMark />
            <span className="brand-text">Plateo</span>
          </Link>
          <nav className="landing-links">
            <a href="#features">Features</a>
            <a href="#how-it-works">How it works</a>
            <a href="#about">About</a>
          </nav>
          <div className="landing-actions">
            {user ? (
              <Link to="/" className="btn btn-primary btn-sm">
                Open Plateo
              </Link>
            ) : (
              <>
                <Link
                  to="/login"
                  className="btn btn-link btn-sm text-decoration-none"
                >
                  Log in
                </Link>
                <Link to="/register" className="btn btn-primary btn-sm">
                  Sign up
                </Link>
              </>
            )}
          </div>
        </div>
      </header>

      <section className="landing-hero">
        <div className="landing-container">
          <div className="landing-hero-copy">
            <LogoWordmark className="landing-hero-mark" />
            <h1>Job hunting, made easier.</h1>
            <p className="landing-lead">
              Find jobs, tailor your resume, fill in applications and keep track
              of every one, without retyping the same details again and again.
            </p>
            <div className="landing-cta">
              {primaryCta}
              {!user && (
                <Link to="/login" className="btn btn-outline-primary btn-lg">
                  Log in
                </Link>
              )}
            </div>
            <p className="landing-note">
              <i className="bi bi-shield-check me-1" />
              Free to track jobs. Nothing is ever submitted without you.
            </p>
          </div>
          <Shot
            src="/landing/home.webp"
            alt="The Plateo home page with job stats and next steps"
            className="landing-hero-shot"
          />
        </div>
      </section>

      <section className="landing-section" id="what">
        <div className="landing-container">
          <div className="landing-heading">
            <h2>What Plateo does</h2>
            <p>
              Everything between “I found a job” and “I got the offer”, without
              the copy and paste.
            </p>
          </div>
          <div className="row g-4">
            {PILLARS.map((p) => (
              <div className="col-md-4" key={p.title}>
                <div className="landing-pillar">
                  <span className="landing-icon">
                    <i className={`bi ${p.icon}`} />
                  </span>
                  <h3>{p.title}</h3>
                  <p>{p.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="landing-section landing-alt" id="features">
        <div className="landing-container">
          <div className="landing-heading">
            <h2>Features</h2>
            <p>The tools you need for every step of an application.</p>
          </div>
          <div className="row g-3">
            {FEATURES.map((f) => (
              <div className="col-sm-6 col-lg-3" key={f.title}>
                <div className="landing-feature">
                  {f.platypus ? <PlatypusLogo size={36} className="landing-platypus" /> : <i className={`bi ${f.icon}`} />}
                  <h3>{f.title}</h3>
                  <p>{f.text}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="landing-showcase">
            <div className="landing-showcase-copy">
              <h3>Fill in applications in one click</h3>
              <p>
                Open an application from Plateo and the extension fills in your
                details, attaches your tailored resume and answers the
                questions. It keeps going on every page of multi-step forms.
              </p>
              {extensionUrl && (
                <a
                  href={extensionUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="btn btn-outline-primary"
                >
                  <i className="bi bi-browser-chrome me-2" />
                  Add to Chrome
                </a>
              )}
            </div>
            <Shot
              src="/landing/filled.webp"
              alt="A job application filled in by the Plateo extension"
            />
          </div>

          <div className="landing-showcase reverse">
            <div className="landing-showcase-copy">
              <h3>Every application in one place</h3>
              <p>
                Drag jobs between stages, see the next step for each one, and
                let Plateo move a job to Applied when it sees the confirmation
                page.
              </p>
            </div>
            <Shot
              src="/landing/tracker.webp"
              alt="The Plateo tracker board with jobs grouped by stage"
            />
          </div>
        </div>
      </section>

      <section className="landing-section" id="how-it-works">
        <div className="landing-container">
          <div className="landing-heading">
            <h2>How it works</h2>
            <p>Set up once, then each application takes minutes.</p>
          </div>
          <div className="landing-steps-wrap">
            <ol className="landing-steps">
              {STEPS.map((s, i) => (
                <li key={s.title}>
                  <span className="landing-step-num">{i + 1}</span>
                  <div>
                    <h3>{s.title}</h3>
                    <p>{s.text}</p>
                  </div>
                </li>
              ))}
            </ol>
            <div className="landing-steps-aside">
              <img
                src="/landing/popup.webp"
                alt="The Plateo extension popup on a job posting"
                loading="lazy"
              />
              <p>
                Click the Plateo icon on any job posting to save it or fill in
                its application.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="landing-section landing-alt" id="about">
        <div className="landing-container">
          <div className="landing-author">
            <img
              src="/author.png"
              alt={OPERATOR}
              className="landing-author-photo"
            />
            <div>
              <span className="landing-eyebrow">About the author</span>
              <h2>Hi, I’m {OPERATOR.split(" ")[0]}.</h2>
              <p>
                I built Plateo because I was tired of looking for jobs again and
                again. Every week it was the same routine: searching the same
                job boards, typing the same details into every application form,
                and rewriting my resume and cover letter for each role.
              </p>
              <p>
                Plateo takes care of those repetitive parts, so applying is
                quicker and you can spend your time on the jobs you actually
                want. I hope it makes your job search a little easier too.
              </p>
              <p className="landing-author-sign">
                {OPERATOR}, creator of Plateo
                {contactEmail && (
                  <>
                    {" · "}
                    <a href={`mailto:${contactEmail}`}>{contactEmail}</a>
                  </>
                )}
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="landing-final">
        <div className="landing-container">
          <h2>
            {user ? "Back to your job search?" : "Ready to make your job search easier?"}
          </h2>
          <p>
            {user
              ? "Pick up where you left off."
              : "Create a free account and add your first job in a couple of minutes."}
          </p>
          <div className="landing-cta justify-content-center">
            {user ? (
              <Link to="/" className="btn btn-light btn-lg">
                Open Plateo
              </Link>
            ) : (
              <>
                <Link to="/register" className="btn btn-light btn-lg">
                  Sign up free
                </Link>
                <Link to="/login" className="btn btn-outline-light btn-lg">
                  Log in
                </Link>
              </>
            )}
          </div>
        </div>
      </section>

      <footer className="landing-footer">
        <div className="landing-container landing-footer-inner">
          <span className="brand">
            <LogoMark />
            <span className="brand-text">Plateo</span>
          </span>
          <nav>
            <Link to="/terms">Terms</Link>
            <Link to="/privacy">Privacy</Link>
            {!user && <Link to="/login">Log in</Link>}
          </nav>
          <span className="text-muted small">
            © {new Date().getFullYear()} {OPERATOR}
          </span>
        </div>
      </footer>
    </div>
  );
}

export default Landing;
